import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../api/client';
import {
  BookOpen, Plus, Search, Filter, X, Calendar, DollarSign,
  Building2, FileText, CheckCircle, XCircle, Link2, Unlink,
  AlertTriangle, ArrowRight, Trash2, Pencil, Clock, Sparkles,
  Coins
} from 'lucide-react';
import TablePagination from '../components/TablePagination';
import SortableTh from '../components/SortableTh';

const STATUS_CONFIG = {
  Active: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Clock, label: 'Active' },
  Matched: { color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Link2, label: 'Matched' },
  Settled: { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle, label: 'Settled' },
  Cancelled: { color: 'bg-gray-100 text-gray-500 border-gray-200', icon: XCircle, label: 'Cancelled' },
};

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.Active;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
      <Icon size={12} /> {config.label}
    </span>
  );
}

function formatCurrency(amount, currency = 'GBP') {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount || 0);
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════
export default function AccrualsPage() {
  const { currentCompanyId } = useAuth();
  const [searchParams] = useSearchParams();
  const [accruals, setAccruals] = useState([]);
  const [stats, setStats] = useState({ active: { count: 0, amount: 0 }, matched: { count: 0, amount: 0 }, settled: { count: 0, amount: 0 }, cancelled: { count: 0, amount: 0 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'Active');
  const [searchTerm, setSearchTerm] = useState('');

  // Sync from URL (notification task links)
  useEffect(() => {
    const s = searchParams.get('status');
    if (s) setStatusFilter(s);
  }, [searchParams]);

  // Sort state
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  // Pagination state
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAccrual, setEditingAccrual] = useState(null);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [matchingAccrual, setMatchingAccrual] = useState(null);

  // Lookups
  const [vendors, setVendors] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Fetch accruals
  const fetchAccruals = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (searchTerm) params.append('search', searchTerm);

      const res = await apiClient.get(`/accruals?${params}`);
      setAccruals(res.data.accruals || []);
      setStats(res.data.stats || stats);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm]);

  // Fetch lookups
  const fetchLookups = useCallback(async () => {
    try {
      const [vRes, gRes, dRes] = await Promise.all([
        apiClient.get('/vendors'),
        apiClient.get('/gl-accounts'),
        apiClient.get('/departments'),
      ]);
      setVendors(vRes.data.vendors || vRes.data || []);
      setGlAccounts(gRes.data.glAccounts || gRes.data.accounts || gRes.data || []);
      setDepartments(dRes.data.departments || dRes.data || []);
    } catch (err) {
      console.error('Fetch lookups error:', err);
    }
  }, []);

  useEffect(() => { fetchAccruals(); }, [fetchAccruals]);
  useEffect(() => { fetchLookups(); }, [fetchLookups]);
  // Reset page on filter/search changes
  useEffect(() => { setPage(1); }, [statusFilter, searchTerm]);

  // Actions
  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this accrual? This cannot be undone.')) return;
    try {
      await apiClient.post(`/accruals/${id}/cancel`);
      fetchAccruals();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this accrual permanently?')) return;
    try {
      await apiClient.delete(`/accruals/${id}`);
      fetchAccruals();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  const sortedAccruals = [...accruals].sort((a, b) => {
    if (!sortField) return 0;
    let aVal, bVal;
    switch (sortField) {
      case 'EstimatedAmount':
        aVal = parseFloat(a.EstimatedAmount) || 0;
        bVal = parseFloat(b.EstimatedAmount) || 0;
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      case 'AccrualDate':
        aVal = a.AccrualDate ? new Date(a.AccrualDate).getTime() : 0;
        bVal = b.AccrualDate ? new Date(b.AccrualDate).getTime() : 0;
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      default:
        aVal = (a[sortField] || '').toString();
        bVal = (b[sortField] || '').toString();
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
  });

  const handleUnmatch = async (id) => {
    if (!window.confirm('Remove invoice match? The accrual will return to Active status.')) return;
    try {
      await apiClient.post(`/accruals/${id}/unmatch`);
      fetchAccruals();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const handleSettle = async (id) => {
    if (!window.confirm('Settle this accrual? This marks the invoice as having replaced the accrual.')) return;
    try {
      await apiClient.post(`/accruals/${id}/settle`);
      fetchAccruals();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-blue-100 p-2 rounded-lg">
            <BookOpen className="text-blue-600" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Accruals</h1>
            <p className="text-sm text-gray-500">Record costs when goods/services are received before the invoice arrives</p>
          </div>
        </div>
        <button
          onClick={() => { setEditingAccrual(null); setShowCreateModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={18} /> New Accrual
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { key: 'active', label: 'Active', icon: Clock, color: 'blue' },
          { key: 'matched', label: 'Matched', icon: Link2, color: 'amber' },
          { key: 'settled', label: 'Settled', icon: CheckCircle, color: 'green' },
          { key: 'cancelled', label: 'Cancelled', icon: XCircle, color: 'gray' },
        ].map(({ key, label, icon: Icon, color }) => (
          <button
            key={key}
            onClick={() => setStatusFilter(key.charAt(0).toUpperCase() + key.slice(1))}
            className={`bg-white rounded-lg shadow p-4 text-left transition-all border-l-4 border-${color}-500 hover:shadow-md ${
              statusFilter.toLowerCase() === key ? `ring-2 ring-${color}-400` : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{label}</p>
                <p className="text-2xl font-bold text-gray-900">{stats[key]?.count || 0}</p>
                <p className={`text-sm font-semibold text-${color}-600 mt-0.5`}>{formatCurrency(stats[key]?.amount)}</p>
              </div>
              <Icon size={28} className={`text-${color}-500`} />
            </div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search vendor, description, PO..."
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Matched">Matched</option>
          <option value="Settled">Settled</option>
          <option value="Cancelled">Cancelled</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center gap-2 text-red-700 text-sm">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
       <div className="overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center text-gray-400">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
            Loading accruals...
          </div>
        ) : accruals.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <BookOpen size={40} className="mx-auto mb-3 opacity-50" />
            <p className="font-medium">No accruals found</p>
            <p className="text-sm mt-1">Create your first accrual to start tracking pre-invoice costs</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <SortableTh field="Status" sortBy={sortField} sortDir={sortDir} onSort={handleSort}>Status</SortableTh>
                <SortableTh field="VendorName" sortBy={sortField} sortDir={sortDir} onSort={handleSort}>Vendor</SortableTh>
                <SortableTh field="Description" sortBy={sortField} sortDir={sortDir} onSort={handleSort}>Description</SortableTh>
                <SortableTh field="EstimatedAmount" sortBy={sortField} sortDir={sortDir} onSort={handleSort} align="right">Amount</SortableTh>
                <SortableTh field="AccrualDate" sortBy={sortField} sortDir={sortDir} onSort={handleSort}>Accrual Date</SortableTh>
                <SortableTh field="GLAccountCode" sortBy={sortField} sortDir={sortDir} onSort={handleSort}>GL Account</SortableTh>
                <SortableTh field="PurchaseOrder" sortBy={sortField} sortDir={sortDir} onSort={handleSort}>PO</SortableTh>
                <SortableTh field="MatchedInvoiceNumber" sortBy={sortField} sortDir={sortDir} onSort={handleSort}>Matched Invoice</SortableTh>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedAccruals.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map(a => (
                <AccrualRow
                  key={a.AccrualID}
                  accrual={a}
                  onEdit={() => { setEditingAccrual(a); setShowCreateModal(true); }}
                  onCancel={() => handleCancel(a.AccrualID)}
                  onDelete={() => handleDelete(a.AccrualID)}
                  onMatch={() => { setMatchingAccrual(a); setShowMatchModal(true); }}
                  onUnmatch={() => handleUnmatch(a.AccrualID)}
                  onSettle={() => handleSettle(a.AccrualID)}
                />
              ))}
            </tbody>
          </table>
        )}
       </div>
       <TablePagination page={page} pageSize={PAGE_SIZE} total={sortedAccruals.length} onPageChange={setPage} />
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <AccrualFormModal
          accrual={editingAccrual}
          vendors={vendors}
          glAccounts={glAccounts}
          departments={departments}
          onClose={() => { setShowCreateModal(false); setEditingAccrual(null); }}
          onSaved={() => { setShowCreateModal(false); setEditingAccrual(null); fetchAccruals(); }}
        />
      )}

      {/* Match Modal */}
      {showMatchModal && matchingAccrual && (
        <MatchModal
          accrual={matchingAccrual}
          onClose={() => { setShowMatchModal(false); setMatchingAccrual(null); }}
          onMatched={() => { setShowMatchModal(false); setMatchingAccrual(null); fetchAccruals(); }}
        />
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// ACCRUAL ROW
// ═══════════════════════════════════════════════════════════════════════════
function AccrualRow({ accrual: a, onEdit, onCancel, onDelete, onMatch, onUnmatch, onSettle }) {
  const variance = a.MatchedInvoiceAmount
    ? parseFloat(a.MatchedInvoiceAmount) - parseFloat(a.EstimatedAmount)
    : null;

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3"><StatusBadge status={a.Status} /></td>
      <td className="px-4 py-3">
        <div className="font-medium text-gray-900">{a.VendorName}</div>
        {a.DeptName && <div className="text-xs text-gray-400">{a.DeptName}</div>}
      </td>
      <td className="px-4 py-3">
        <div className="text-gray-700 max-w-[200px] truncate" title={a.Description}>{a.Description}</div>
        {a.Notes && <div className="text-xs text-gray-400 truncate max-w-[200px]" title={a.Notes}>{a.Notes}</div>}
      </td>
      <td className="px-4 py-3 text-right font-mono font-medium text-gray-900">
        {formatCurrency(a.EstimatedAmount, a.Currency)}
      </td>
      <td className="px-4 py-3 text-gray-600">{formatDate(a.AccrualDate)}</td>
      <td className="px-4 py-3">
        {a.GLAccountCode ? (
          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{a.GLAccountCode}</span>
        ) : '—'}
      </td>
      <td className="px-4 py-3">
        {a.PurchaseOrder ? (
          <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded">{a.PurchaseOrder}</span>
        ) : '—'}
      </td>
      <td className="px-4 py-3">
        {a.MatchedInvoiceNumber ? (
          <div>
            <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded font-medium">
              {a.MatchedInvoiceNumber}
            </span>
            {variance !== null && (
              <div className={`text-xs mt-0.5 ${variance === 0 ? 'text-green-600' : variance > 0 ? 'text-red-500' : 'text-blue-500'}`}>
                {variance === 0 ? 'Exact match' : `Variance: ${formatCurrency(variance, a.Currency)}`}
              </div>
            )}
          </div>
        ) : (
          <span className="text-xs text-gray-400">Not matched</span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-center gap-1">
          {a.Status === 'Active' && (
            <>
              <button onClick={onMatch} title="Match with invoice" className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors">
                <Link2 size={15} />
              </button>
              <button onClick={onEdit} title="Edit" className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                <Pencil size={15} />
              </button>
              <button onClick={onCancel} title="Cancel" className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors">
                <XCircle size={15} />
              </button>
              <button onClick={onDelete} title="Delete" className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors">
                <Trash2 size={15} />
              </button>
            </>
          )}
          {a.Status === 'Matched' && (
            <>
              <button onClick={onSettle} title="Settle — invoice replaces accrual" className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors">
                <CheckCircle size={15} />
              </button>
              <button onClick={onUnmatch} title="Remove match" className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors">
                <Unlink size={15} />
              </button>
            </>
          )}
          {a.Status === 'Settled' && (
            <span className="text-xs text-green-600 font-medium">Complete</span>
          )}
          {a.Status === 'Cancelled' && (
            <button onClick={onDelete} title="Delete permanently" className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors">
              <Trash2 size={15} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// CREATE / EDIT MODAL
// ═══════════════════════════════════════════════════════════════════════════
function AccrualFormModal({ accrual, vendors, glAccounts, departments, onClose, onSaved }) {
  const isEdit = !!accrual;
  const [form, setForm] = useState({
    vendorId: accrual?.VendorID || '',
    vendorName: accrual?.VendorName || '',
    description: accrual?.Description || '',
    estimatedAmount: accrual?.EstimatedAmount || '',
    currency: accrual?.Currency || 'GBP',
    glAccountId: accrual?.GLAccountID || '',
    glAccountCode: accrual?.GLAccountCode || '',
    glAccountName: accrual?.GLAccountName || '',
    departmentId: accrual?.DepartmentID || '',
    departmentName: accrual?.DepartmentName || '',
    accrualDate: accrual?.AccrualDate ? accrual.AccrualDate.substring(0, 10) : new Date().toISOString().substring(0, 10),
    expectedInvoiceDate: accrual?.ExpectedInvoiceDate ? accrual.ExpectedInvoiceDate.substring(0, 10) : '',
    purchaseOrder: accrual?.PurchaseOrder || '',
    notes: accrual?.Notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleVendorChange = (vendorId) => {
    if (!vendorId) {
      setForm(f => ({ ...f, vendorId: '', vendorName: '' }));
      return;
    }
    const v = vendors.find(v => v.VendorID === vendorId);
    if (v) setForm(f => ({ ...f, vendorId: v.VendorID, vendorName: v.VendorName }));
  };

  const handleGLChange = (glId) => {
    if (!glId) {
      setForm(f => ({ ...f, glAccountId: '', glAccountCode: '', glAccountName: '' }));
      return;
    }
    const g = glAccounts.find(g => g.GLAccountID === glId);
    if (g) setForm(f => ({ ...f, glAccountId: g.GLAccountID, glAccountCode: g.AccountCode, glAccountName: g.AccountName }));
  };

  const handleDeptChange = (deptId) => {
    if (!deptId) {
      setForm(f => ({ ...f, departmentId: '', departmentName: '' }));
      return;
    }
    const d = departments.find(d => d.DepartmentID === deptId);
    if (d) setForm(f => ({ ...f, departmentId: d.DepartmentID, departmentName: d.DepartmentName || d.Name }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        await apiClient.put(`/accruals/${accrual.AccrualID}`, form);
      } else {
        await apiClient.post('/accruals', form);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <BookOpen size={20} className="text-blue-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">{isEdit ? 'Edit Accrual' : 'New Accrual'}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-center gap-2">
              <AlertTriangle size={16} /> {error}
            </div>
          )}

          {/* Vendor */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vendor *</label>
              <select
                value={form.vendorId}
                onChange={(e) => handleVendorChange(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select vendor...</option>
                {vendors.map(v => (
                  <option key={v.VendorID} value={v.VendorID}>{v.VendorName}</option>
                ))}
              </select>
              {!form.vendorId && (
                <input
                  type="text"
                  value={form.vendorName}
                  onChange={(e) => setForm(f => ({ ...f, vendorName: e.target.value }))}
                  placeholder="Or type vendor name..."
                  className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <select
                value={form.departmentId}
                onChange={(e) => handleDeptChange(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select department...</option>
                {departments.map(d => (
                  <option key={d.DepartmentID} value={d.DepartmentID}>{d.DepartmentName || d.Name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="e.g. Consulting services for Q1 2026"
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Amount + Currency + GL */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Amount *</label>
              <div className="relative">
                <Coins size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.estimatedAmount}
                  onChange={(e) => setForm(f => ({ ...f, estimatedAmount: e.target.value }))}
                  required
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <select
                value={form.currency}
                onChange={(e) => setForm(f => ({ ...f, currency: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="GBP">GBP</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="NOK">NOK</option>
                <option value="SEK">SEK</option>
                <option value="DKK">DKK</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">GL Account</label>
              <select
                value={form.glAccountId}
                onChange={(e) => handleGLChange(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select GL...</option>
                {glAccounts.map(g => (
                  <option key={g.GLAccountID} value={g.GLAccountID}>{g.AccountCode} — {g.AccountName}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Accrual Date *</label>
              <input
                type="date"
                value={form.accrualDate}
                onChange={(e) => setForm(f => ({ ...f, accrualDate: e.target.value }))}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expected Invoice Date</label>
              <input
                type="date"
                value={form.expectedInvoiceDate}
                onChange={(e) => setForm(f => ({ ...f, expectedInvoiceDate: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Order</label>
              <input
                type="text"
                value={form.purchaseOrder}
                onChange={(e) => setForm(f => ({ ...f, purchaseOrder: e.target.value }))}
                placeholder="PO number..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2}
              placeholder="Additional context..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Accrual explanation */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
            <p className="font-medium mb-1">How accruals work:</p>
            <p>When you receive goods/services before the invoice, the accrual records the estimated cost in the correct period. Once the actual invoice arrives, match it here — the invoice replaces the accrual. The accrual record stays for audit trail.</p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm flex items-center gap-2"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <CheckCircle size={16} />
              )}
              {isEdit ? 'Update Accrual' : 'Create Accrual'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// MATCH MODAL — Match an invoice to the accrual
// ═══════════════════════════════════════════════════════════════════════════
function MatchModal({ accrual, onClose, onMatched }) {
  const [invoices, setInvoices] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch suggestions + all unmatched invoices in parallel
        const [sugRes, invRes] = await Promise.all([
          apiClient.get(`/accruals/suggest-matches/${accrual.AccrualID}`),
          apiClient.get('/accruals/unmatched/invoices?vendorName='),
        ]);
        setSuggestions(sugRes.data.suggestions || []);
        setInvoices(invRes.data || []);
      } catch (err) {
        console.error('Fetch match data error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [accrual.AccrualID]);

  const handleMatch = async (invoiceId) => {
    setMatching(true);
    try {
      await apiClient.post(`/accruals/${accrual.AccrualID}/match`, { invoiceId });
      onMatched();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setMatching(false);
    }
  };

  const filteredInvoices = searchTerm
    ? invoices.filter(i =>
        (i.InvoiceNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (i.VendorName || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : invoices;

  const suggestedIds = new Set(suggestions.map(s => s.InvoiceID));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Link2 size={20} className="text-amber-500" /> Match Invoice to Accrual
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              <span className="font-medium">{accrual.VendorName}</span> — {formatCurrency(accrual.EstimatedAmount, accrual.Currency)} — {accrual.Description}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-8 text-gray-400">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto mb-3"></div>
              Finding matching invoices...
            </div>
          ) : (
            <>
              {/* Suggested matches */}
              {suggestions.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-amber-700 flex items-center gap-2 mb-2">
                    <Sparkles size={16} /> Suggested Matches (vendor + amount)
                  </h3>
                  <div className="space-y-2">
                    {suggestions.map(inv => (
                      <InvoiceMatchCard
                        key={inv.InvoiceID}
                        invoice={inv}
                        accrualAmount={accrual.EstimatedAmount}
                        currency={accrual.Currency}
                        isSuggestion={true}
                        onMatch={() => handleMatch(inv.InvoiceID)}
                        matching={matching}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* All unmatched invoices */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">All Available Invoices</h3>
                <div className="relative mb-3">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search invoices..."
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {filteredInvoices.filter(i => !suggestedIds.has(i.InvoiceID)).map(inv => (
                    <InvoiceMatchCard
                      key={inv.InvoiceID}
                      invoice={inv}
                      accrualAmount={accrual.EstimatedAmount}
                      currency={accrual.Currency}
                      isSuggestion={false}
                      onMatch={() => handleMatch(inv.InvoiceID)}
                      matching={matching}
                    />
                  ))}
                  {filteredInvoices.filter(i => !suggestedIds.has(i.InvoiceID)).length === 0 && (
                    <p className="text-center text-gray-400 text-sm py-4">No invoices available for matching</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// INVOICE MATCH CARD
// ═══════════════════════════════════════════════════════════════════════════
function InvoiceMatchCard({ invoice, accrualAmount, currency, isSuggestion, onMatch, matching }) {
  const variance = parseFloat(invoice.TotalAmount) - parseFloat(accrualAmount);
  const variancePercent = accrualAmount ? ((variance / accrualAmount) * 100).toFixed(1) : 0;

  return (
    <div className={`border rounded-lg p-3 flex items-center justify-between ${
      isSuggestion ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white hover:bg-gray-50'
    } transition-colors`}>
      <div className="flex items-center gap-4">
        <div className="text-left">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 text-sm">{invoice.InvoiceNumber}</span>
            {isSuggestion && (
              <span className="text-xs bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded font-medium">Suggested</span>
            )}
          </div>
          <div className="text-xs text-gray-500">{invoice.VendorName} • {formatDate(invoice.InvoiceDate)}</div>
        </div>
        <div className="text-right">
          <div className="font-mono font-medium text-sm">{formatCurrency(invoice.TotalAmount, currency)}</div>
          <div className={`text-xs ${variance === 0 ? 'text-green-600' : Math.abs(variance) < accrualAmount * 0.05 ? 'text-amber-600' : 'text-red-500'}`}>
            {variance === 0 ? '✓ Exact match' : `${variance > 0 ? '+' : ''}${formatCurrency(variance, currency)} (${variancePercent}%)`}
          </div>
        </div>
      </div>
      <button
        onClick={onMatch}
        disabled={matching}
        className="ml-4 px-3 py-1.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 text-sm flex items-center gap-1.5 transition-colors"
      >
        <ArrowRight size={14} /> Match
      </button>
    </div>
  );
}
